import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Inbox, Phone, Mail, RefreshCw, Loader2, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin({ user }) {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/inquiries`, { withCredentials: true })
      .then((res) => {
        setInquiries(res.data);
        setForbidden(false);
      })
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
        else if (err?.response?.status === 401) navigate("/login");
        else toast.error("Could not load enquiries.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      /* ignore */
    }
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0b0a] grain text-[#eae7e0]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a0b0a]/70 border-b border-[#eae7e0]/10">
        <div className="flex items-center justify-between px-6 md:px-12 h-20">
          <div>
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#b89b72]">
              MRS Builders
            </p>
            <h1 className="font-display text-2xl tracking-wide">Enquiries</h1>
          </div>
          <div className="flex items-center gap-4">
            {user?.picture && (
              <img
                src={user.picture}
                alt={user?.name}
                className="w-9 h-9 rounded-full ring-1 ring-[#eae7e0]/20"
              />
            )}
            <span className="hidden sm:block text-sm text-[#9ca399]" data-testid="admin-user-email">
              {user?.name || user?.email}
            </span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#eae7e0]/15 text-[11px] tracking-[0.18em] uppercase text-[#9ca399] hover:text-[#eae7e0] hover:border-[#b89b72]/50 transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-12 py-10 md:py-14 max-w-6xl mx-auto">
        {forbidden ? (
          <div
            data-testid="admin-forbidden"
            className="flex flex-col items-center text-center py-24"
          >
            <ShieldAlert size={40} className="text-[#b89b72] mb-6" strokeWidth={1} />
            <h2 className="font-display text-3xl mb-3">Access restricted</h2>
            <p className="text-[#9ca399] max-w-md">
              Your account ({user?.email}) isn&apos;t on the authorised list. Ask
              the owner to add your email to the admin allowlist.
            </p>
            <button
              onClick={handleLogout}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-[#b89b72] text-[#0a0b0a] text-[11px] tracking-[0.2em] uppercase"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3 text-[#9ca399]">
                <Inbox size={18} className="text-[#b89b72]" />
                <span data-testid="admin-inquiry-count">
                  {inquiries.length} total enquir{inquiries.length === 1 ? "y" : "ies"}
                </span>
              </div>
              <button
                onClick={load}
                data-testid="admin-refresh-button"
                className="inline-flex items-center gap-2 text-sm text-[#9ca399] hover:text-[#eae7e0] transition-colors"
              >
                <RefreshCw size={15} /> Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex items-center gap-3 text-[#9ca399] py-24 justify-center">
                <Loader2 size={20} className="animate-spin text-[#b89b72]" /> Loading enquiries…
              </div>
            ) : inquiries.length === 0 ? (
              <div className="text-center py-24 text-[#9ca399]" data-testid="admin-empty">
                No enquiries yet. New submissions from the website will appear here.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4" data-testid="admin-inquiry-list">
                {inquiries.map((q, i) => (
                  <motion.div
                    key={q.inquiry_id || i}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.03, ease: [0.16, 1, 0.3, 1] }}
                    data-testid={`admin-inquiry-${i}`}
                    className="bg-[#121412] border border-[#eae7e0]/10 p-6 hover:border-[#b89b72]/40 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-display text-2xl">{q.name}</h3>
                          {q.service && (
                            <span className="text-[10px] tracking-[0.18em] uppercase bg-[#b89b72]/15 text-[#b89b72] px-3 py-1">
                              {q.service}
                            </span>
                          )}
                        </div>
                        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-[#9ca399]">
                          <a href={`tel:${q.phone}`} className="inline-flex items-center gap-2 hover:text-[#eae7e0]">
                            <Phone size={14} className="text-[#b89b72]" /> {q.phone}
                          </a>
                          {q.email && (
                            <a href={`mailto:${q.email}`} className="inline-flex items-center gap-2 hover:text-[#eae7e0]">
                              <Mail size={14} className="text-[#b89b72]" /> {q.email}
                            </a>
                          )}
                        </div>
                        {q.message && (
                          <p className="mt-4 text-[#eae7e0]/85 leading-relaxed max-w-2xl">
                            {q.message}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-[#9ca399]/70 whitespace-nowrap">
                        {fmtDate(q.created_at)}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
